import { Code, Copy, CheckCircle } from 'lucide-react';
import { useState } from 'react';

export default function ApiDocs() {
  const [copiedEndpoint, setCopiedEndpoint] = useState<string | null>(null);

  const copyToClipboard = (text: string, endpoint: string) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(endpoint);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const endpoints = [
    {
      method: 'GET',
      path: '/api/address/:address',
      title: 'Check Address',
      description: 'Check if an address should be blacklisted',
      example: `curl https://your-app.pages.dev/api/address/0x1234...`,
      response: `{
  "address": "0x1234...",
  "shouldBlacklist": false,
  "riskRating": "low",
  "riskScore": 25,
  "reasons": [],
  "relatedAddresses": [],
  "poolAddress": null
}`,
    },
    {
      method: 'POST',
      path: '/api/addresses/lookup',
      title: 'Bulk Lookup',
      description: 'Check multiple addresses at once',
      example: `curl -X POST https://your-app.pages.dev/api/addresses/lookup \\
  -H "Content-Type: application/json" \\
  -d '{"addresses": ["0x123...", "0x456..."]}'`,
      response: `{
  "results": [
    {
      "address": "0x123...",
      "riskRating": "safe",
      "riskScore": 10
    }
  ]
}`,
    },
    {
      method: 'POST',
      path: '/api/scan',
      title: 'Start Scan',
      description: 'Initiate a blockchain scan',
      example: `curl -X POST https://your-app.pages.dev/api/scan \\
  -H "Content-Type: application/json" \\
  -d '{
    "tokenAddress": "0x66B0FB...",
    "chain": "polygon",
    "maxHops": 15
  }'`,
      response: `{
  "scanId": "scan_abc123",
  "status": "running",
  "message": "Scan started successfully"
}`,
    },
    {
      method: 'GET',
      path: '/api/scan/:scanId',
      title: 'Get Scan Status',
      description: 'Check the status of a running scan',
      example: `curl https://your-app.pages.dev/api/scan/scan_abc123`,
      response: `{
  "scanId": "scan_abc123",
  "status": "running",
  "progress": 67,
  "addressesFound": 1247,
  "poolsDetected": 12
}`,
    },
    {
      method: 'POST',
      path: '/api/export',
      title: 'Export Addresses',
      description: 'Export blacklist in CSV or JSON format',
      example: `curl -X POST https://your-app.pages.dev/api/export \\
  -H "Content-Type: application/json" \\
  -d '{"riskRating": "high", "format": "csv"}'`,
      response: `address,risk_score,risk_rating,reasons
0x123...,85,high,"Multiple hops, Pool convergence"`,
    },
  ];

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          API Documentation
        </h1>
        <p className="text-gray-400 text-lg">
          Integrate abuse detection into your application
        </p>
      </div>

      {/* Getting Started */}
      <div className="mb-8 p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
        <h2 className="text-2xl font-bold text-white mb-4">Getting Started</h2>
        <p className="text-gray-300 mb-4">
          The Token Abuse Monitor API allows you to check addresses for abuse patterns in real-time. All endpoints return JSON responses.
        </p>
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
          <p className="text-sm text-gray-300">
            <strong className="text-blue-400">Base URL:</strong> <code className="text-blue-300">https://your-app.pages.dev</code>
          </p>
        </div>
      </div>

      {/* Authentication */}
      <div className="mb-8 p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
        <h2 className="text-2xl font-bold text-white mb-4">Authentication</h2>
        <p className="text-gray-300 mb-4">
          Currently, the API is open for testing. In production, you'll need to add authentication headers.
        </p>
        <div className="p-4 rounded-lg bg-gray-900/50 border border-gray-700">
          <code className="text-sm text-green-400">
            Authorization: Bearer YOUR_API_KEY
          </code>
        </div>
      </div>

      {/* Endpoints */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">API Endpoints</h2>
        
        {endpoints.map((endpoint, index) => (
          <div key={index} className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-lg font-semibold text-sm ${
                endpoint.method === 'GET' 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              }`}>
                {endpoint.method}
              </span>
              <code className="text-gray-300 font-mono">{endpoint.path}</code>
            </div>

            {/* Title & Description */}
            <h3 className="text-xl font-semibold text-white mb-2">{endpoint.title}</h3>
            <p className="text-gray-400 mb-4">{endpoint.description}</p>

            {/* Example Request */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-300">Example Request</h4>
                <button
                  onClick={() => copyToClipboard(endpoint.example, `${endpoint.method}-${endpoint.path}`)}
                  className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-all"
                >
                  {copiedEndpoint === `${endpoint.method}-${endpoint.path}` ? (
                    <>
                      <CheckCircle size={14} className="text-green-400" />
                      <span className="text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} className="text-gray-400" />
                      <span className="text-gray-400">Copy</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="p-4 rounded-lg bg-gray-900/50 border border-gray-700 overflow-x-auto">
                <code className="text-sm text-green-400">{endpoint.example}</code>
              </pre>
            </div>

            {/* Example Response */}
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Example Response</h4>
              <pre className="p-4 rounded-lg bg-gray-900/50 border border-gray-700 overflow-x-auto">
                <code className="text-sm text-blue-400">{endpoint.response}</code>
              </pre>
            </div>
          </div>
        ))}
      </div>

      {/* Integration Example */}
      <div className="mt-8 p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm">
        <h2 className="text-2xl font-bold text-white mb-4">Integration Example</h2>
        <p className="text-gray-300 mb-4">
          Here's how to integrate address checking into your reward distribution:
        </p>
        <pre className="p-4 rounded-lg bg-gray-900/50 border border-gray-700 overflow-x-auto">
          <code className="text-sm text-purple-400">{`// Before sending reward
async function checkAddress(userAddress) {
  const response = await fetch(
    \`https://your-app.pages.dev/api/address/\${userAddress}\`
  );
  const data = await response.json();
  
  if (data.riskRating === 'high' || data.riskRating === 'critical') {
    console.log('Blocked:', data.reasons);
    return false; // Don't send reward
  }
  
  return true; // Safe to send
}

// Usage
if (await checkAddress(user.walletAddress)) {
  await sendReward(user.walletAddress, amount);
} else {
  await logBlockedAttempt(user.walletAddress);
}`}</code>
        </pre>
      </div>

      {/* Rate Limits */}
      <div className="mt-8 p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
        <h3 className="text-lg font-semibold text-yellow-300 mb-2">Rate Limits</h3>
        <p className="text-gray-300">
          API requests are limited to <strong>100 requests per minute</strong> per IP address. 
          Bulk lookup endpoint allows up to <strong>100 addresses per request</strong>.
        </p>
      </div>

      {/* Support */}
      <div className="mt-8 p-6 rounded-xl bg-blue-500/10 border border-blue-500/30">
        <h3 className="text-lg font-semibold text-blue-300 mb-2">Need Help?</h3>
        <p className="text-gray-300">
          Check the <a href="/settings" className="text-blue-400 hover:text-blue-300 underline">Settings</a> page to configure detection thresholds, 
          or visit the <a href="/lookup" className="text-blue-400 hover:text-blue-300 underline">Address Lookup</a> page to test addresses manually.
        </p>
      </div>
    </div>
  );
}

